import type { ReactNode } from 'react';
import styled from '@emotion/styled';

import { MAPPED_BY_DESIGNER } from 'features/purchase-orders/types';
import { Avatar } from 'components/avatar/Avatar';
import { Text } from 'components/text/Text';

export type FacilitySubsupplierDisplayProps = {
  facilityName: string;
  subsupplierName?: string;
  subsupplierAvatarColor?: string;
  /** Designer review: opens supplier detail drawer when subsupplier row is clicked. */
  onSubsupplierClick?: () => void;
};

/** Facility name primary; subsupplier name + avatar as child — matches map-suppliers OptionDisplay. */
export const FacilitySubsupplierDisplay = ({
  facilityName,
  subsupplierName = '',
  subsupplierAvatarColor = '',
  onSubsupplierClick,
}: FacilitySubsupplierDisplayProps) => {
  const displayFacility = facilityName.trim();
  const displaySubsupplier = subsupplierName.trim();
  const isAnonymousDesigner =
    displaySubsupplier === MAPPED_BY_DESIGNER && displaySubsupplier !== displayFacility;

  if (!displayFacility && !displaySubsupplier) {
    return null;
  }

  const showSubsupplierChild =
    Boolean(displayFacility && displaySubsupplier) || isAnonymousDesigner;
  const avatarColor = subsupplierAvatarColor?.includes('#')
    ? subsupplierAvatarColor
    : subsupplierAvatarColor
      ? `#${subsupplierAvatarColor}`
      : '';

  return (
    <Wrap>
      <FacilityName variant="bodyXs" weight="semiBold" color="secondary" noWrap>
        {displayFacility || displaySubsupplier}
      </FacilityName>
      {showSubsupplierChild ? (
        <MaybeClickable onClick={onSubsupplierClick}>
          <SubsupplierLine>
            {isAnonymousDesigner ? (
              <Avatar size="xs" color="#bdbdbd" src="/broken-image.jpg" />
            ) : (
              <>
                <Avatar size="xs" letters={displaySubsupplier} color={avatarColor} />
                <Text variant="bodyXs" color="placeholder" noWrap>
                  {displaySubsupplier}
                </Text>
              </>
            )}
          </SubsupplierLine>
        </MaybeClickable>
      ) : null}
    </Wrap>
  );
};

const MaybeClickable = ({ onClick, children }: { onClick?: () => void; children: ReactNode }) =>
  onClick ? (
    <ClickableLine onClick={onClick} role="button" tabIndex={0}>
      {children}
    </ClickableLine>
  ) : (
    children
  );

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const FacilityName = styled(Text)`
  line-height: 1.25;
`;

const SubsupplierLine = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  min-width: 0;
`;

const ClickableLine = styled.div`
  display: flex;
  min-width: 0;
  cursor: pointer;

  &:hover span {
    text-decoration: underline;
  }
`;
